'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Topology, GeometryCollection } from 'topojson-specification';
import type { Feature, Geometry } from 'geojson';
import type { RichTextField } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import TransitionLink from './TransitionLink';
import './regional-footprint.css';

/**
 * "Our regional footprint": a Mercator map of East and Southeast Asia with arcs
 * drawing out from Hong Kong, a chip picker ("Where do you operate?") and a
 * panel whose lines are generated from the selection. Ported from the
 * OnePacific Claude Design comp "Regional Footprint Hub".
 *
 * Geometry, coverage model (team / affiliate / partner / network) and tax
 * flags live here, keyed by market code. Every visible string comes from
 * props so the slice can be fully translated in Prismic.
 */

export type MarketCode = 'cn' | 'tw' | 'kr' | 'jp' | 'ph' | 'vn' | 'th' | 'my' | 'sg' | 'id' | 'in' | 'au' | 'na';
export const MARKET_CODES: MarketCode[] = ['cn', 'tw', 'kr', 'jp', 'ph', 'vn', 'th', 'my', 'sg', 'id', 'in', 'au', 'na'];

type Via = 'team' | 'affiliate' | 'partner' | 'network';
interface Geo {
  /** world-atlas numeric country id, when the country is on the map */
  id?: string;
  /** arc destination (lon, lat) */
  ll?: [number, number];
  /** city-state marker instead of a filled country */
  dot?: [number, number];
  via: Via;
  tax: boolean;
  anchor?: 'start' | 'end';
  dx?: number;
  dy?: number;
  /** off-map market: label pinned to a map edge */
  edge?: 'in' | 'au' | 'na';
}

const HK: [number, number] = [114.17, 22.32];
const GEO: Record<MarketCode, Geo> = {
  cn: { id: '156', ll: [121.47, 31.23], via: 'team', tax: true, anchor: 'start', dx: 8, dy: -6 },
  tw: { id: '158', ll: [121.56, 25.03], via: 'affiliate', tax: true, anchor: 'start', dx: 10, dy: -6 },
  kr: { id: '410', ll: [126.98, 37.57], via: 'affiliate', tax: true, anchor: 'end', dx: -9, dy: 4 },
  jp: { id: '392', ll: [139.69, 35.69], via: 'affiliate', tax: false, anchor: 'start', dx: 9, dy: 4 },
  ph: { id: '608', ll: [120.98, 14.6], via: 'team', tax: true, anchor: 'start', dx: 9, dy: 4 },
  vn: { id: '704', ll: [106.66, 10.76], via: 'affiliate', tax: true, anchor: 'start', dx: 8, dy: 16 },
  th: { id: '764', ll: [100.5, 13.76], via: 'team', tax: true, anchor: 'end', dx: -9, dy: 14 },
  my: { id: '458', ll: [101.69, 3.14], via: 'team', tax: true, anchor: 'end', dx: -9, dy: 4 },
  sg: { dot: [103.82, 1.35], ll: [103.82, 1.35], via: 'team', tax: false, anchor: 'start', dx: 9, dy: 10 },
  id: { id: '360', ll: [106.85, -6.21], via: 'team', tax: true, anchor: 'start', dx: 6, dy: 14 },
  in: { via: 'partner', tax: true, edge: 'in' },
  au: { via: 'partner', tax: false, edge: 'au' },
  na: { via: 'network', tax: false, edge: 'na' },
};
const ARC_CODES = MARKET_CODES.filter((c) => GEO[c].ll);
const ID_TO_CODE: Record<string, MarketCode> = {};
for (const c of MARKET_CODES) if (GEO[c].id) ID_TO_CODE[GEO[c].id as string] = c;

const W = 720;
const H = 720;
const BBOX = {
  type: 'Polygon' as const,
  coordinates: [
    [
      [94, -12],
      [94, 44],
      [147, 44],
      [147, -12],
      [94, -12],
    ],
  ],
};

export interface FootprintMarket {
  code: MarketCode;
  name: string;
  city?: string;
  language?: string;
  partner?: string;
  note?: string;
}

export interface FootprintTemplates {
  /** "{n} markets" — panel title when more than three are selected */
  markets: string;
  /** "We've deployed NetSuite in all {n}." */
  deployedAll: string;
  /** "We've deployed NetSuite in {list}." */
  deployedList: string;
  /** "We've deployed NetSuite in {n} of these." */
  deployedCount: string;
  /** "Local tax and statutory setup for {list}." ({list} = market codes) */
  tax: string;
  /** "Consultants who speak {list}." */
  languages: string;
  /** "{markets} delivered with our affiliate teams in {cities}." */
  affiliates: string;
  /** "{markets} covered through our local partners {partners}." */
  partners: string;
  /** "local teams in {n}" — compact summary for five or more markets */
  localTeams: string;
  /** "partner teams in {n}" */
  partnerTeams: string;
}

export interface RegionalFootprintProps {
  eyebrow?: string;
  title?: string;
  titleAccent?: string;
  lede?: RichTextField;
  question?: string;
  questionHint?: string;
  homeLabel: string;
  homeTag?: string;
  emptyText?: string;
  templates: FootprintTemplates;
  /** "and" — joins the last item of a list */
  andWord: string;
  closingLine?: string;
  /** "{n}" = number of selected markets including home */
  ctaLabel?: string;
  ctaHref?: string;
  ctaTarget?: string;
  footnote?: RichTextField;
  /** Chip order follows this array */
  markets: FootprintMarket[];
  defaultSelected?: MarketCode[];
  /** Path to the world-atlas countries TopoJSON */
  topoUrl?: string;
}

const fill = (tpl: string, vars: Record<string, string | number>) =>
  tpl.replace(/\{(\w+)\}/g, (_, k: string) => (k in vars ? String(vars[k]) : `{${k}}`));

const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

function useInView(ref: React.RefObject<Element | null>, threshold = 0.25) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = setTimeout(() => setInView(true), 0);
      return () => clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold]);
  return inView;
}

export default function RegionalFootprint({
  eyebrow,
  title,
  titleAccent,
  lede,
  question,
  questionHint,
  homeLabel,
  homeTag,
  emptyText,
  templates: T,
  andWord,
  closingLine,
  ctaLabel,
  ctaHref,
  ctaTarget,
  footnote,
  markets,
  defaultSelected = ['cn', 'sg'],
  topoUrl = '/data/countries-110m.json',
}: RegionalFootprintProps) {
  const byCode = useMemo(() => {
    const m = new Map<MarketCode, FootprintMarket>();
    for (const mk of markets) if (GEO[mk.code]) m.set(mk.code, mk);
    return m;
  }, [markets]);

  const [sel, setSel] = useState<MarketCode[]>(() => defaultSelected.filter((c) => byCode.has(c)));
  const toggle = (c: MarketCode) =>
    setSel((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  // --- map data ---
  const [countries, setCountries] = useState<Feature<Geometry>[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(topoUrl)
      .then((r) => r.json())
      .then((topo: Topology<{ countries: GeometryCollection }>) => {
        if (cancelled) return;
        const fc = feature(topo, topo.objects.countries);
        setCountries(fc.features as Feature<Geometry>[]);
      })
      .catch(() => {
        if (!cancelled) setCountries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [topoUrl]);

  const proj = useMemo(() => geoMercator().fitSize([W, H], BBOX), []);
  const path = useMemo(() => geoPath(proj), [proj]);
  const pt = (ll: [number, number]) => proj(ll) as [number, number];
  const [hx, hy] = pt(HK);

  // --- reveal sequence: home marker → arcs stagger → side panel ---
  const rootRef = useRef<HTMLElement>(null);
  const inView = useInView(rootRef);
  const [homeIn, setHomeIn] = useState(false);
  const [drawn, setDrawn] = useState(0);
  const [sideIn, setSideIn] = useState(false);
  useEffect(() => {
    if (!inView || !countries) return;
    const reduce =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timers: ReturnType<typeof setTimeout>[] = [];
    if (reduce) {
      // No sequence: show the finished state on the next tick.
      timers.push(
        setTimeout(() => {
          setHomeIn(true);
          setDrawn(ARC_CODES.length);
          setSideIn(true);
        }, 0)
      );
    } else {
      timers.push(setTimeout(() => setHomeIn(true), 200));
      ARC_CODES.forEach((_, i) => timers.push(setTimeout(() => setDrawn(i + 1), 600 + i * 130)));
      timers.push(setTimeout(() => setSideIn(true), 600 + ARC_CODES.length * 130 + 500));
    }
    return () => timers.forEach(clearTimeout);
  }, [inView, countries]);

  const picking = sel.length > 0;
  const isSel = (c: MarketCode) => sel.includes(c);

  // --- panel copy ---
  // Latin "and" → "a, b and c"; CJK conjunction (及 / 和) → "a、b及c".
  const latin = /^[A-Za-z]/.test(andWord);
  const list = (a: string[]) =>
    a.length < 2
      ? a.join('')
      : `${a.slice(0, -1).join(latin ? ', ' : '、')}${latin ? ` ${andWord} ` : andWord}${a[a.length - 1]}`;
  const chosen = sel.map((c) => byCode.get(c)!).filter(Boolean);
  const total = chosen.length + 1; // + home
  const via = (v: Via) => chosen.filter((m) => GEO[m.code].via === v);
  const local = via('team');
  const aff = via('affiliate');
  const ptn = via('partner');
  const net = via('network');
  const deployed = [...local, ...aff];

  let panelTitle: React.ReactNode;
  const lines: string[] = [];
  if (!chosen.length) {
    panelTitle = homeLabel;
  } else {
    panelTitle =
      chosen.length > 3 ? (
        <>
          {homeLabel}
          <span className="rf-plus">+</span>
          <span className="rf-n">{fill(T.markets, { n: chosen.length })}</span>
        </>
      ) : (
        [homeLabel, ...chosen.map((m) => m.name)].map((n, i) => (
          <span key={n}>
            {i > 0 && <span className="rf-plus">+</span>}
            {n}
          </span>
        ))
      );
    if (chosen.length > 4) {
      if (deployed.length) {
        lines.push(
          deployed.length === chosen.length
            ? fill(T.deployedAll, { n: chosen.length })
            : fill(T.deployedCount, { n: deployed.length })
        );
      }
      const parts: string[] = [];
      if (local.length) parts.push(fill(T.localTeams, { n: local.length }));
      if (aff.length + ptn.length) parts.push(fill(T.partnerTeams, { n: aff.length + ptn.length }));
      if (parts.length) lines.push(`${cap(parts.join(', '))}.`);
    } else {
      if (deployed.length) {
        lines.push(
          deployed.length === chosen.length
            ? fill(T.deployedAll, { n: total })
            : fill(T.deployedList, { list: list(deployed.map((m) => m.name)) })
        );
      }
      const tax = chosen.filter((m) => GEO[m.code].tax).map((m) => m.code.toUpperCase());
      if (tax.length) lines.push(fill(T.tax, { list: list(tax) }));
      const langs = [...new Set(deployed.map((m) => m.language).filter(Boolean) as string[])];
      if (langs.length) lines.push(fill(T.languages, { list: list(langs) }));
      if (aff.length) {
        lines.push(
          fill(T.affiliates, {
            markets: list(aff.map((m) => m.name)),
            cities: list(aff.map((m) => m.city || m.name)),
          })
        );
      }
      if (ptn.length) {
        lines.push(
          fill(T.partners, {
            markets: list(ptn.map((m) => m.name)),
            partners: list(ptn.map((m) => m.partner || m.name)),
          })
        );
      }
    }
    for (const m of net) if (m.note) lines.push(m.note);
  }

  const selectedCodes = ['hk', ...sel].join(',');
  const href = ctaHref ? `${ctaHref}${ctaHref.includes('?') ? '&' : '?'}markets=${selectedCodes}` : undefined;
  const isInternal = !!href && href.startsWith('/') && !ctaTarget;

  return (
    <section ref={rootRef} className={`rf${picking ? ' rf-picking' : ''}`}>
      <div className="rf-inner">
        {eyebrow && <p className="rf-eyebrow">{eyebrow}</p>}
        {(title || titleAccent) && (
          <h2 className="rf-title">
            {title} {titleAccent && <span className="rf-accent">{titleAccent}</span>}
          </h2>
        )}
        {lede && (
          <div className="rf-lede">
            <PrismicRichText field={lede} />
          </div>
        )}

        <div className="rf-grid">
          <div className="rf-map" aria-hidden>
            <svg viewBox={`0 0 ${W} ${H}`}>
              <g>
                {countries?.map((f) => {
                  const code = ID_TO_CODE[String(f.id)];
                  return (
                    <path
                      key={String(f.id)}
                      className={`rf-country${code && isSel(code) ? ' sel' : ''}`}
                      d={path(f) || undefined}
                    />
                  );
                })}
              </g>
              {(['in', 'au', 'na'] as const).map((e) => {
                const m = byCode.get(e);
                if (!m) return null;
                const txt = e === 'in' ? `← ${m.name}` : e === 'au' ? `${m.name} ↘` : `${m.name} →`;
                const x = e === 'in' ? 10 : W - 10;
                const y = e === 'in' ? pt([94, 22])[1] : e === 'au' ? H - 12 : 20;
                return (
                  <text
                    key={e}
                    className={`rf-edge${isSel(e) ? ' sel' : ''}`}
                    x={x}
                    y={y}
                    textAnchor={e === 'in' ? 'start' : 'end'}
                  >
                    {txt}
                  </text>
                );
              })}
              {GEO.sg.dot && (
                <circle
                  className={`rf-dot${isSel('sg') ? ' sel' : ''}`}
                  cx={pt(GEO.sg.dot)[0]}
                  cy={pt(GEO.sg.dot)[1]}
                  r={5}
                />
              )}
              <circle className="rf-dot rf-dot-home" cx={hx} cy={hy} r={5} />
              <g>
                {ARC_CODES.map((c, i) => {
                  const g = GEO[c];
                  const m = byCode.get(c);
                  if (!m || !g.ll) return null;
                  const [x, y] = pt(g.ll);
                  const mx = (hx + x) / 2;
                  const my = (hy + y) / 2;
                  const dx = x - hx;
                  const dy = y - hy;
                  const d = Math.hypot(dx, dy) || 1;
                  const bend = 0.22 * d;
                  const cx = mx - (dy / d) * bend;
                  const cy = my + (dx / d) * bend;
                  const on = i < drawn;
                  const s = isSel(c);
                  const cls = (base: string) => `${base}${on ? ' drawn' : ''}${s ? ' sel' : ''}`;
                  return (
                    <g key={c}>
                      <path className={cls('rf-arc')} pathLength={1} d={`M${hx},${hy} Q${cx},${cy} ${x},${y}`} />
                      <circle className={cls('rf-end')} cx={x} cy={y} r={3} />
                      <text className={cls('rf-lbl')} x={x + (g.dx ?? 0)} y={y + (g.dy ?? 0)} textAnchor={g.anchor}>
                        {m.city || m.name}
                      </text>
                    </g>
                  );
                })}
              </g>
              <g className={`rf-home${homeIn ? ' in' : ''}`} transform={`translate(${hx},${hy})`}>
                <circle className="rf-ring" r={10} />
                <circle className="rf-ring rf-ring2" r={10} />
                <circle className="rf-core" r={6} />
                <text x={-16} y={22} textAnchor="end">
                  {homeLabel}
                </text>
              </g>
            </svg>
          </div>

          <div className={`rf-side${sideIn ? ' in' : ''}`}>
            {question && (
              <p className="rf-q">
                {question}
                {questionHint && <small>{questionHint}</small>}
              </p>
            )}
            <div className="rf-chips" role="group" aria-label={question}>
              <button type="button" className="rf-chip rf-chip-lock" aria-pressed disabled>
                {homeLabel}
                {homeTag && <em>{homeTag}</em>}
              </button>
              {markets
                .filter((m) => GEO[m.code])
                .map((m) => (
                  <button
                    key={m.code}
                    type="button"
                    className={`rf-chip${isSel(m.code) ? ' on' : ''}`}
                    aria-pressed={isSel(m.code)}
                    onClick={() => toggle(m.code)}
                  >
                    {m.name}
                  </button>
                ))}
            </div>
            <div className="rf-panel" aria-live="polite">
              <h3>{panelTitle}</h3>
              {!chosen.length ? (
                emptyText && <p className="rf-empty">{emptyText}</p>
              ) : (
                <>
                  <ul>
                    {lines.map((l, i) => (
                      <li key={i}>{l}</li>
                    ))}
                    {closingLine && <li className="rf-hk">{closingLine}</li>}
                  </ul>
                  {ctaLabel &&
                    href &&
                    (isInternal ? (
                      <TransitionLink href={href} className="rf-cta">
                        {fill(ctaLabel, { n: total })}
                      </TransitionLink>
                    ) : (
                      <a href={href} target={ctaTarget} rel={ctaTarget === '_blank' ? 'noopener noreferrer' : undefined} className="rf-cta">
                        {fill(ctaLabel, { n: total })}
                      </a>
                    ))}
                </>
              )}
            </div>
          </div>
        </div>

        {footnote && (
          <div className="rf-foot">
            <PrismicRichText field={footnote} />
          </div>
        )}
      </div>
    </section>
  );
}
